'use client';

import { getOrCreateId } from '@repo/platform-utils/static-ids';
import { cloneElement, forwardRef, useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import { useAnalyticsPageView } from '../../components/PageView/Context';
import { useAnalytics } from '../../hooks/useAnalytics';
import { warnOnDevIfCloneElementWillFail } from '../../lib/utils';
import { useSegmentStandardTrack } from '../lib/useSegmentStandardTrack';

type LinkViewedProps = {
  entityName: string;
  destinationUrl: string;
  linkCopy: string;
  sectionName?: string;
  linkPosition?: number;
  additionalAttributes?: Record<string, unknown>;
  eventId?: string;
  scrollableAncestor?: HTMLElement;
  children: React.JSX.Element;
};

export const LinkViewed = forwardRef<HTMLElement, LinkViewedProps>(
  (
    {
      entityName,
      destinationUrl,
      linkCopy,
      sectionName,
      linkPosition,
      additionalAttributes,
      eventId,
      scrollableAncestor,
      children,
    },
    ref,
  ) => {
    const { deviceId } = useAnalytics();
    const { pageViewReady } = useAnalyticsPageView();
    const track = useSegmentStandardTrack('Link Viewed');
    const generatedEventIdRef = useRef('');
    const generatedEventId = getOrCreateId(generatedEventIdRef);
    const { ref: inViewRef, inView } = useInView({
      root: scrollableAncestor,
      threshold: 0.01,
      rootMargin: '1px',
      triggerOnce: true,
    });

    const setRefs = (node: HTMLElement | null) => {
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref && 'current' in ref) {
        ref.current = node;
      }
      inViewRef(node);
    };

    useEffect(() => {
      if (!pageViewReady || !deviceId || !inView) {
        return;
      }

      void track(
        {
          entity_name: entityName,
          destination_url: destinationUrl,
          link_copy: linkCopy,
          section_name: sectionName,
          link_position: linkPosition,
        },
        { eventId: eventId ?? generatedEventId, additionalAttributes },
      );
    }, [pageViewReady, deviceId, inView]);

    warnOnDevIfCloneElementWillFail(children, 'LinkViewed');
    return cloneElement(children as React.ReactElement<{ ref: unknown }>, { ref: setRefs });
  },
);
